// client/src/admin/popups/AdminPopups.js
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Campaign as PopupIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as DialogIcon,
  ViewStream as BannerIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  LockClock as PermanentIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";

const lang_defaults = {
  adminPopups: "Popup Manager",
  adminPopupsSubtitle: "Create and manage popups shown to users",
  newPopup: "New Popup",
  editPopup: "Edit Popup",
  title: "Title",
  content: "Content (HTML supported)",
  type: "Display Type",
  dialog: "Dialog Popup",
  topBar: "Top Bar Banner",
  isPermanent: "Permanent (always show)",
  targetType: "Target Audience",
  all: "All Users",
  specific: "Specific User",
  targetUser: "Target User",
  isActive: "Active",
  save: "Save",
  cancel: "Cancel",
  delete: "Delete",
  noPopups: "No popups yet",
  bgColor: "Background Color (optional)",
  textColor: "Text Color (optional)",
  permanent: "Permanent",
  oneTime: "One-time",
  confirmDelete: "Delete this popup?",
};

const AdminPopups = ({ lang: propLang }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const { lang: ctxLang } = useContext(TranslateContext);
  const t = (k) => propLang?.[k] || ctxLang?.[k] || lang_defaults[k] || k;

  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    popup: null,
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const emptyForm = {
    popup_id: null,
    title: "",
    content: "",
    type: "dialog",
    is_permanent: "no",
    target_type: "all",
    target_uid: null,
    is_active: "yes",
    bg_color: "",
    text_color: "",
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchPopups();
  }, []);

  async function fetchPopups() {
    setLoading(true);
    const res = await hitAxios({
      path: "/api/popup/admin/list",
      admin: true,
      post: false,
      showLoading: false,
    });
    setLoading(false);
    if (res?.data?.success) setPopups(res.data.data || []);
  }

  async function fetchUsers() {
    setUsersLoading(true);
    const res = await hitAxios({
      path: "/api/admin/users/list",
      admin: true,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    setUsersLoading(false);
    if (res?.data?.success) setUsers(res.data.data || []);
  }

  function openCreate() {
    setForm(emptyForm);
    setDialogOpen(true);
    fetchUsers();
  }

  function openEdit(popup) {
    setForm({
      popup_id: popup.popup_id,
      title: popup.title || "",
      content: popup.content || "",
      type: popup.type || "dialog",
      is_permanent: popup.is_permanent || "no",
      target_type: popup.target_type || "all",
      target_uid: popup.target_uid || null,
      is_active: popup.is_active || "yes",
      bg_color: popup.bg_color || "",
      text_color: popup.text_color || "",
    });
    setDialogOpen(true);
    fetchUsers();
  }

  async function handleSave() {
    const res = await hitAxios({
      path: "/api/popup/admin/save",
      admin: true,
      post: true,
      obj: form,
    });
    if (res?.data?.success) {
      setDialogOpen(false);
      fetchPopups();
    }
  }

  async function handleDelete() {
    const res = await hitAxios({
      path: "/api/popup/admin/delete",
      admin: true,
      post: true,
      obj: { popup_id: deleteDialog.popup?.popup_id },
    });
    if (res?.data?.success) {
      setDeleteDialog({ open: false, popup: null });
      fetchPopups();
    }
  }

  async function handleToggle(popup) {
    const newStatus = popup.is_active === "yes" ? "no" : "yes";
    const res = await hitAxios({
      path: "/api/popup/admin/toggle",
      admin: true,
      post: true,
      obj: { popup_id: popup.popup_id, is_active: newStatus },
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setPopups((prev) =>
        prev.map((p) =>
          p.popup_id === popup.popup_id ? { ...p, is_active: newStatus } : p,
        ),
      );
    }
  }

  const selectedUser = users.find((u) => u.uid === form.target_uid) || null;

  return (
    <Box>
      <PageHeader
        title={t("adminPopups")}
        subtitle={t("adminPopupsSubtitle")}
        icon={PopupIcon}
        primaryAction={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {t("newPopup")}
          </Button>
        }
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={24} sx={{ color: "primary.main" }} />
        </Box>
      ) : popups.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <PopupIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography
            variant="body1"
            fontWeight={600}
            color="text.secondary"
            mb={1}
          >
            {t("noPopups")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ borderRadius: 2, textTransform: "none", mt: 1 }}
          >
            {t("newPopup")}
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {popups.map((popup) => {
            const isActive = popup.is_active === "yes";
            const isDialog = popup.type === "dialog";
            const isPermanent = popup.is_permanent === "yes";
            const isSpecific = popup.target_type === "specific";

            return (
              <Paper
                key={popup.popup_id}
                elevation={0}
                sx={{
                  p: 2.5,
                  border: `1px solid ${isActive ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider}`,
                  borderRadius: 2,
                  bgcolor: isActive
                    ? alpha(theme.palette.primary.main, 0.03)
                    : "background.paper",
                  transition: "all 0.15s",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1.5}
                >
                  {/* Left info */}
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1.5}
                    flex={1}
                    minWidth={0}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        bgcolor: alpha(
                          isDialog
                            ? theme.palette.primary.main
                            : theme.palette.warning.main,
                          0.1,
                        ),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {isDialog ? (
                        <DialogIcon
                          sx={{
                            fontSize: 20,
                            color: isDialog ? "primary.main" : "warning.main",
                          }}
                        />
                      ) : (
                        <BannerIcon
                          sx={{ fontSize: 20, color: "warning.main" }}
                        />
                      )}
                    </Box>

                    <Box flex={1} minWidth={0}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                        gap={0.5}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="text.primary"
                          noWrap
                        >
                          {popup.title}
                        </Typography>
                        <Chip
                          label={isDialog ? t("dialog") : t("topBar")}
                          size="small"
                          color={isDialog ? "primary" : "warning"}
                          sx={{ fontSize: "0.6rem", height: 18 }}
                        />
                        <Chip
                          label={isPermanent ? t("permanent") : t("oneTime")}
                          size="small"
                          color={isPermanent ? "error" : "default"}
                          sx={{ fontSize: "0.6rem", height: 18 }}
                        />
                        {isSpecific && (
                          <Chip
                            icon={
                              <PersonIcon
                                sx={{ fontSize: "10px !important" }}
                              />
                            }
                            label={
                              popup.target_user_name ||
                              popup.target_uid ||
                              "Specific User"
                            }
                            size="small"
                            color="info"
                            sx={{ fontSize: "0.6rem", height: 18 }}
                          />
                        )}
                        <Chip
                          label={isActive ? "Active" : "Inactive"}
                          size="small"
                          color={isActive ? "success" : "default"}
                          sx={{ fontSize: "0.6rem", height: 18 }}
                        />
                      </Stack>

                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{
                          mt: 0.5,
                          display: "block",
                          maxWidth: 480,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.7rem",
                        }}
                      >
                        {popup.content?.replace(/<[^>]*>/g, "").slice(0, 100)}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Right actions */}
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isActive}
                          onChange={() => handleToggle(popup)}
                          size="small"
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                    <Tooltip title={t("editPopup")}>
                      <IconButton
                        size="small"
                        onClick={() => openEdit(popup)}
                        sx={{
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("delete")}>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteDialog({ open: true, popup })}
                        sx={{
                          color: "error.main",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* ── Create / Edit Dialog ── */}
      <CommonDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={form.popup_id ? t("editPopup") : t("newPopup")}
        icon={PopupIcon}
        maxWidth="sm"
      >
        <Stack spacing={2.5} mt={1}>
          {/* Title */}
          <TextField
            fullWidth
            autoFocus
            label={t("title")}
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />

          {/* Content */}
          <TextField
            fullWidth
            multiline
            rows={5}
            label={t("content")}
            placeholder="<b>Hello!</b> This is a <em>popup</em> message."
            value={form.content}
            onChange={(e) =>
              setForm((p) => ({ ...p, content: e.target.value }))
            }
            helperText="HTML is supported. Use <b>, <em>, <a href=...>, etc."
          />

          <Divider />

          {/* Display Type */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              display="block"
              mb={1}
            >
              {t("type")}
            </Typography>
            <ToggleButtonGroup
              value={form.type}
              exclusive
              onChange={(_, v) => v && setForm((p) => ({ ...p, type: v }))}
              size="small"
            >
              <ToggleButton
                value="dialog"
                sx={{ textTransform: "none", fontSize: "0.78rem", gap: 0.75 }}
              >
                <DialogIcon sx={{ fontSize: 16 }} />
                {t("dialog")}
              </ToggleButton>
              <ToggleButton
                value="topbar"
                sx={{ textTransform: "none", fontSize: "0.78rem", gap: 0.75 }}
              >
                <BannerIcon sx={{ fontSize: 16 }} />
                {t("topBar")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Permanent toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={form.is_permanent === "yes"}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    is_permanent: e.target.checked ? "yes" : "no",
                  }))
                }
              />
            }
            label={
              <Stack>
                <Typography variant="body2" fontWeight={600}>
                  {t("isPermanent")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  If on, this popup will show every time the user visits (cannot
                  be dismissed permanently)
                </Typography>
              </Stack>
            }
          />

          <Divider />

          {/* Target Audience */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              display="block"
              mb={1}
            >
              {t("targetType")}
            </Typography>
            <ToggleButtonGroup
              value={form.target_type}
              exclusive
              onChange={(_, v) =>
                v &&
                setForm((p) => ({ ...p, target_type: v, target_uid: null }))
              }
              size="small"
            >
              <ToggleButton
                value="all"
                sx={{ textTransform: "none", fontSize: "0.78rem", gap: 0.75 }}
              >
                <GroupIcon sx={{ fontSize: 16 }} />
                {t("all")}
              </ToggleButton>
              <ToggleButton
                value="specific"
                sx={{ textTransform: "none", fontSize: "0.78rem", gap: 0.75 }}
              >
                <PersonIcon sx={{ fontSize: 16 }} />
                {t("specific")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Specific user picker */}
          {form.target_type === "specific" && (
            <Autocomplete
              options={users}
              loading={usersLoading}
              getOptionLabel={(u) => `${u.name || ""} (${u.email})`}
              value={selectedUser}
              onChange={(_, v) =>
                setForm((p) => ({ ...p, target_uid: v?.uid || null }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("targetUser")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading && <CircularProgress size={14} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          <Divider />

          {/* Colors (only for topbar) */}
          {form.type === "topbar" && (
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label={t("bgColor")}
                placeholder="#1976d2"
                value={form.bg_color}
                onChange={(e) =>
                  setForm((p) => ({ ...p, bg_color: e.target.value }))
                }
                InputProps={{
                  startAdornment: form.bg_color ? (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: 1,
                        bgcolor: form.bg_color,
                        border: `1px solid ${theme.palette.divider}`,
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                  ) : null,
                }}
              />
              <TextField
                fullWidth
                label={t("textColor")}
                placeholder="#ffffff"
                value={form.text_color}
                onChange={(e) =>
                  setForm((p) => ({ ...p, text_color: e.target.value }))
                }
                InputProps={{
                  startAdornment: form.text_color ? (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: 1,
                        bgcolor: form.text_color,
                        border: `1px solid ${theme.palette.divider}`,
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                  ) : null,
                }}
              />
            </Stack>
          )}

          {/* Active toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active === "yes"}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    is_active: e.target.checked ? "yes" : "no",
                  }))
                }
              />
            }
            label={t("isActive")}
          />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setDialogOpen(false)}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!form.title.trim() || !form.content.trim()}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {t("save")}
            </Button>
          </Stack>
        </Stack>
      </CommonDialog>

      {/* ── Delete Confirm ── */}
      <CommonDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, popup: null })}
        title={t("delete")}
        icon={DeleteIcon}
        maxWidth="xs"
      >
        <Stack spacing={3} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {t("confirmDelete")} <strong>{deleteDialog.popup?.title}</strong>?
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setDeleteDialog({ open: false, popup: null })}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {t("delete")}
            </Button>
          </Stack>
        </Stack>
      </CommonDialog>
    </Box>
  );
};

export default AdminPopups;
